/**
 * Typed client for the WebRTC call endpoints (packages/protocol/src/webrtc.ts).
 * Kept inside the feature so the shared src/api.ts stays untouched.
 */
import type { CallFeedItem, CallGroupDetail, CallGroupSummary, CallGroupsOverview, CallGroupsQuery, CallSummary } from "@backstory/protocol";
import type { Page } from "@backstory/protocol";
import { request } from "../../api";

const enc = encodeURIComponent;

function qs(params: object): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params as Record<string, unknown>)) {
    if (v === undefined || v === null || v === "") continue;
    parts.push(`${enc(k)}=${enc(String(v))}`);
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

// The Calls API shapes now live in @backstory/protocol (api-calls.ts) so the Go
// handlers and the dashboard share one definition.
import type { CallsQuery, CallSeriesPoint, CallMarker, CallDetail, QualityBucketCount, Breakdown, CallsOverview } from "@backstory/protocol";

export type { CallsQuery, CallSeriesPoint, CallMarker, CallDetail, QualityBucketCount, Breakdown, CallsOverview, CallFeedItem, CallGroupDetail, CallGroupSummary, CallGroupsOverview, CallGroupsQuery };


export const callsApi = {
  list: (q: CallsQuery) => request<Page<CallSummary>>(`/v1/calls${qs(q)}`),
  get: (id: string) => request<CallDetail>(`/v1/calls/${enc(id)}`),
  forSession: (sessionId: string) => request<CallSummary[] | { items: CallSummary[] }>(`/v1/sessions/${enc(sessionId)}/calls`).then((r) => (Array.isArray(r) ? r : (r.items ?? []))),
  overview: (q: CallsQuery) => request<CallsOverview>(`/v1/calls/overview${qs(q)}`),
  /** Groups and ungrouped connections, newest first. */
  feed: (q: CallsQuery) => request<Page<CallFeedItem>>(`/v1/call-groups${qs(q)}`),
  groupsOverview: (q: CallGroupsQuery) => request<CallGroupsOverview>(`/v1/call-groups/overview${qs(q)}`),
  group: (id: string) => request<CallGroupDetail>(`/v1/call-groups/${enc(id)}`),
};

export const callKeys = {
  list: (q: CallsQuery) => ["calls", "list", q] as const,
  feed: (q: CallsQuery) => ["calls", "feed", q] as const,
  detail: (id: string) => ["calls", "detail", id] as const,
  forSession: (id: string) => ["calls", "session", id] as const,
  overview: (from: string, to: string, filters?: CallsQuery) => ["calls", "overview", from, to, filters ?? {}] as const,
  groups: (q: CallGroupsQuery) => ["calls", "groups", q] as const,
  groupsOverview: (q: CallGroupsQuery) => ["calls", "groups", "overview", q] as const,
  group: (id: string) => ["calls", "group", id] as const,
};
